/**
 * The S3 kinds of team connection as the staff pages read them (TRA-922):
 * a customer role TrainLoop assumes (`s3_role`, nothing secret stored) or
 * a customer key pair (`s3`). The Connections page assigns them and the
 * Evals config page picks from them, so the kinds, the form and its rules
 * live here, apart from the components, the way githubConnection.ts does.
 *
 * The rules mirror CreateS3RoleConnectionRequest and
 * CreateS3ConnectionRequest (api/app/schemas/internal.py), so a staff
 * member reads what is wrong in the form footer before the API's 422.
 */

import type {
  ConnectionAdminItem,
  CreateS3ConnectionRequest,
  CreateS3RoleConnectionRequest,
  ImpersonatableTeam,
} from '@/api/generated/model'

export type S3ConnectionKind = 's3_role' | 's3'

/** The role comes first: it stores no secret, so it is the one to offer. */
export const S3_CONNECTION_KINDS: readonly S3ConnectionKind[] = ['s3_role', 's3']

export const S3_CONNECTION_KIND_LABELS: Record<S3ConnectionKind, string> = {
  s3_role: 'Assumed role',
  s3: 'Access key pair',
}

export function isS3ConnectionKind(kind: string): kind is S3ConnectionKind {
  return (S3_CONNECTION_KINDS as readonly string[]).includes(kind)
}

/** The connections an S3 source or a batch reads through. */
export function s3ConnectionsOf(connections: ConnectionAdminItem[]): ConnectionAdminItem[] {
  return connections.filter((row) => isS3ConnectionKind(row.kind))
}

/** The api's default, where most customer buckets live. */
export const DEFAULT_S3_REGION = 'us-west-2'

/** One flat form for both kinds; s3ConnectionRequest sends the fields of the picked one. */
export interface S3ConnectionForm {
  kind: S3ConnectionKind
  display_name: string
  region: string
  role_arn: string
  access_key_id: string
  secret_access_key: string
  principal_arn: string
}

export function emptyS3ConnectionForm(team: ImpersonatableTeam): S3ConnectionForm {
  return {
    kind: 's3_role',
    display_name: `${team.name} S3`,
    region: DEFAULT_S3_REGION,
    role_arn: '',
    access_key_id: '',
    secret_access_key: '',
    principal_arn: '',
  }
}

/** The connector may only assume roles named trainloop-* (components/apps/aws-connector.ts). */
const ROLE_ARN = /^arn:aws:iam::\d{12}:role\/trainloop-.+$/
const PRINCIPAL_ARN = /^arn:aws:iam::\d{12}:(user|role)\/.+$/

function validateRole(form: S3ConnectionForm): string | null {
  const roleArn = form.role_arn.trim()
  if (!ROLE_ARN.test(roleArn) || roleArn.length > 128) {
    return 'The role ARN has the form arn:aws:iam::<account id>:role/trainloop-<name>. The connector can only assume roles whose name starts with trainloop-.'
  }
  return null
}

function validateKeyPair(form: S3ConnectionForm): string | null {
  const accessKeyId = form.access_key_id.trim()
  if (accessKeyId.length < 16 || accessKeyId.length > 128) {
    return 'The access key id is 16 to 128 characters.'
  }
  const secret = form.secret_access_key.trim()
  if (secret.length < 16 || secret.length > 256) {
    return 'The secret access key is 16 to 256 characters.'
  }
  const principalArn = form.principal_arn.trim()
  if (!PRINCIPAL_ARN.test(principalArn) || principalArn.length > 2048) {
    return 'The principal ARN has the form arn:aws:iam::<account id>:user/<name>, or role/<name> for a role.'
  }
  return null
}

/**
 * The first thing wrong with the form, or null when it can be sent.
 * `takenNames` holds every connection name on the team, GitHub included:
 * the name is unique per team across kinds.
 */
export function validateS3Connection(
  form: S3ConnectionForm,
  takenNames: readonly string[],
): string | null {
  const name = form.display_name.trim()
  if (name === '') return 'Give the connection a name.'
  if (name.length > 255) return 'The name is at most 255 characters.'
  if (takenNames.includes(name)) return 'This team already has a connection with that name.'
  const region = form.region.trim()
  if (region === '' || region.length > 32) return 'Enter the region the bucket lives in.'
  return form.kind === 's3_role' ? validateRole(form) : validateKeyPair(form)
}

/** The create request for the picked kind. The other kind's fields never leave the page. */
export function s3ConnectionRequest(
  form: S3ConnectionForm,
): CreateS3ConnectionRequest | CreateS3RoleConnectionRequest {
  const shared = { display_name: form.display_name.trim(), region: form.region.trim() }
  if (form.kind === 's3_role') {
    return { kind: 's3_role', ...shared, role_arn: form.role_arn.trim() }
  }
  return {
    kind: 's3',
    ...shared,
    access_key_id: form.access_key_id.trim(),
    secret_access_key: form.secret_access_key.trim(),
    principal_arn: form.principal_arn.trim(),
  }
}
